import React from 'react';
import { Lead } from '../../../types/lead.types';
import { toast } from 'react-hot-toast';
import { updateLead } from '../../../services/lead.service';
import { db, storage } from '../../../config/firebase';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Upload,
  FileText
} from 'lucide-react';

interface LeadDocumentsTabProps {
  lead: Lead;
  editedLead: Lead;
  setEditedLead: React.Dispatch<React.SetStateAction<Lead>>;
  onUpdate: (lead: Lead) => void;
  documents: { label: string; url: string }[];
  loadingDocuments: boolean;
  uploading: boolean;
  setUploading: React.Dispatch<React.SetStateAction<boolean>>;
  onDocumentsReload: () => void;
}

const LeadDocumentsTab: React.FC<LeadDocumentsTabProps> = ({
  lead,
  editedLead,
  setEditedLead,
  onUpdate,
  documents,
  loadingDocuments,
  uploading,
  setUploading,
  onDocumentsReload
}) => {
  const { currentUser } = useAuth();

  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentUser) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      // Create storage reference
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const storageRef = ref(storage, `leads/${lead.id}/${fileName}`);

      // Upload file
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      // Update lead's documents
      const existingOther = editedLead.documents?.other || [];
      const updatedDocuments = {
        ...editedLead.documents,
        other: [...existingOther, downloadUrl]
      };

      await updateLead(lead.id, {
        documents: updatedDocuments,
        lastUpdated: Timestamp.now()
      });

      // Update local state
      const updatedLead = {
        ...editedLead,
        documents: updatedDocuments
      };
      setEditedLead(updatedLead);
      onUpdate(updatedLead);

      // Reload documents to show the new one
      onDocumentsReload();

      toast.success('Document uploaded successfully');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      // Reset the input
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Documents</h3>
        <label className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
          {uploading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Upload Document
            </>
          )}
          <input
            type="file"
            accept="image/*,.pdf,.doc,.docx"
            onChange={handleDocumentUpload}
            disabled={uploading}
            className="hidden"
          />
        </label>
      </div>

      {loadingDocuments ? (
        <div className="flex items-center gap-2 text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></div>
          Loading documents...
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No documents uploaded yet</p>
          <p className="text-gray-400 text-sm mt-1">Click "Upload Document" to add files</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documents.map((doc, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
              <p className="text-sm font-medium text-gray-700 mb-2">{doc.label}</p>
              {doc.url.match(/\.(jpg|jpeg|png|gif|webp)$/i) || doc.url.includes('firebasestorage') ? (
                <a href={doc.url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={doc.url}
                    alt={doc.label}
                    className="w-full h-40 object-cover rounded-md border border-gray-100 hover:opacity-90 transition-opacity"
                  />
                </a>
              ) : (
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 hover:underline"
                >
                  View Document
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadDocumentsTab;
